import React, { useState, useMemo } from 'react';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Printer, 
  Edit2, 
  Trash2, 
  Eye, 
  X, 
  PlusCircle, 
  MinusCircle, 
  UserCheck, 
  FileCheck,
  Building,
  Calendar,
  AlertCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { format } from 'date-fns';
import { Client, Invoice, Signatory, Settings } from '../types';
import { cn } from '../lib/utils';

// Local TypeScript interfaces
export interface CustomerCareTechnicalReport {
  id: string;
  type: 'technical' | 'service';
  reportNumber: string;
  proformaId?: string;
  proformaNumber?: string;
  clientId?: string;
  clientName: string;
  date: string;
  subject: string;
  items: {
    sn: number;
    typeOfAC: string;
    qty: number;
    userDept: string;
    checkingResult?: string;
    performanceStatus?: string;
    remedialRecommendations: string;
  }[];
  signatoryId: string;
  signatoryName: string;
  signatoryTitle: string;
  signatureUrl?: string;
  createdAt: any;
  createdBy: string;
  creatorName: string;
}

interface TechServiceReportsSectionProps {
  reports: CustomerCareTechnicalReport[];
  proformas: Invoice[];
  clients: Client[];
  signatories: Signatory[];
  settings: Settings | null;
  onSaveReport: (report: Partial<CustomerCareTechnicalReport>) => Promise<void>;
  onDeleteReport: (id: string) => Promise<void>;
  onExportPDF: (report: CustomerCareTechnicalReport, isPrint?: boolean) => void;
}

export const TechServiceReportsSection: React.FC<TechServiceReportsSectionProps> = ({
  reports,
  proformas,
  clients,
  signatories,
  settings,
  onSaveReport,
  onDeleteReport,
  onExportPDF
}) => {
  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'technical' | 'service'>('all');
  
  // Modal States
  const [isOpenFormModal, setIsOpenFormModal] = useState(false);
  const [formType, setFormType] = useState<'technical' | 'service'>('technical');
  const [editingReport, setEditingReport] = useState<CustomerCareTechnicalReport | null>(null);
  const [viewingReport, setViewingReport] = useState<CustomerCareTechnicalReport | null>(null);

  // Form States
  const [selectedProformaId, setSelectedProformaId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [customClientName, setCustomClientName] = useState('');
  const [reportDate, setReportDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [subject, setSubject] = useState('');
  const [selectedSignatoryId, setSelectedSignatoryId] = useState('');
  const [items, setItems] = useState<any[]>([
    { sn: 1, typeOfAC: '', qty: 1, userDept: '', checkingResult: '', performanceStatus: '', remedialRecommendations: '' }
  ]);

  // Open form for a new report
  const handleOpenNewReport = (type: 'technical' | 'service') => {
    setFormType(type);
    setEditingReport(null);
    setSelectedProformaId('');
    setSelectedClientId('');
    setCustomClientName('');
    setReportDate(format(new Date(), 'yyyy-MM-dd'));
    setSubject(
      type === 'technical' 
        ? 'TECHNICAL REPORT FOR AIR CONDITIONERS SERVICE' 
        : 'REPORT AFTER SERVICE OF AIR CONDITIONERS'
    );
    setSelectedSignatoryId(signatories[0]?.id || '');
    setItems([
      { sn: 1, typeOfAC: '', qty: 1, userDept: '', checkingResult: '', performanceStatus: '', remedialRecommendations: '' }
    ]);
    setIsOpenFormModal(true);
  };

  // Open form for editing
  const handleOpenEditReport = (report: CustomerCareTechnicalReport) => {
    setFormType(report.type);
    setEditingReport(report);
    setSelectedProformaId(report.proformaId || '');
    
    // Determine client selection state
    const matchedClient = clients.find(c => c.name === report.clientName);
    if (matchedClient) {
      setSelectedClientId(matchedClient.id);
      setCustomClientName('');
    } else {
      setSelectedClientId('custom');
      setCustomClientName(report.clientName);
    }

    setReportDate(report.date);
    setSubject(report.subject);
    setSelectedSignatoryId(report.signatoryId);
    setItems(report.items.map(item => ({
      ...item,
      checkingResult: item.checkingResult || '',
      performanceStatus: item.performanceStatus || ''
    })));
    setIsOpenFormModal(true);
  };

  // Handle Proforma Selection in Technical Report
  const handleProformaChange = (proformaId: string) => {
    setSelectedProformaId(proformaId);
    if (!proformaId) return;

    const proforma = proformas.find(p => p.id === proformaId);
    if (proforma) {
      // Auto-fill Client Name (find match in clients list or use raw string)
      const matchedClient = clients.find(c => c.id === proforma.clientId || c.name === proforma.clientName);
      if (matchedClient) {
        setSelectedClientId(matchedClient.id);
        setCustomClientName('');
      } else {
        setSelectedClientId('custom');
        setCustomClientName(proforma.clientName || '');
      }

      // Auto fill date
      if (proforma.createdAt) {
        try {
          const pDate = (proforma.createdAt as any).toDate ? (proforma.createdAt as any).toDate() : new Date(proforma.createdAt as any);
          setReportDate(format(pDate, 'yyyy-MM-dd'));
        } catch (e) {
          setReportDate(format(new Date(), 'yyyy-MM-dd'));
        }
      }
    }
  };

  // Add Item Row to Form
  const handleAddItemRow = () => {
    setItems(prev => [
      ...prev,
      { 
        sn: prev.length + 1, 
        typeOfAC: '', 
        qty: 1, 
        userDept: '', 
        checkingResult: '', 
        performanceStatus: '', 
        remedialRecommendations: '' 
      }
    ]);
  };

  // Remove Item Row from Form
  const handleRemoveItemRow = (index: number) => {
    if (items.length <= 1) return;
    const updated = items.filter((_, idx) => idx !== index).map((item, idx) => ({
      ...item,
      sn: idx + 1
    }));
    setItems(updated);
  };

  // Handle Item field updates
  const handleItemFieldChange = (index: number, field: string, value: any) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Form Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Get client name
    let clientName = '';
    if (selectedClientId === 'custom') {
      clientName = customClientName;
    } else {
      const matched = clients.find(c => c.id === selectedClientId);
      clientName = matched ? matched.name : '';
    }

    if (!clientName) {
      alert("Please select or enter a Client Name.");
      return;
    }

    // Get signatory
    const signatory = signatories.find(s => s.id === selectedSignatoryId);
    if (!signatory) {
      alert("Please select a Signatory.");
      return;
    }

    // Prepare report data
    const selectedProforma = proformas.find(p => p.id === selectedProformaId);
    const cleanedItems = items.map(item => {
      const clean: any = {
        sn: item.sn,
        typeOfAC: item.typeOfAC,
        qty: parseInt(item.qty as any) || 1,
        userDept: item.userDept,
        remedialRecommendations: item.remedialRecommendations
      };
      if (formType === 'technical') {
        clean.checkingResult = item.checkingResult;
      } else {
        clean.performanceStatus = item.performanceStatus;
      }
      return clean;
    });

    const reportData: Partial<CustomerCareTechnicalReport> = {
      type: formType,
      proformaId: formType === 'technical' ? selectedProformaId : undefined,
      proformaNumber: formType === 'technical' ? (selectedProforma?.invoiceNumber || undefined) : undefined,
      clientId: selectedClientId !== 'custom' ? selectedClientId : undefined,
      clientName,
      date: reportDate,
      subject,
      items: cleanedItems,
      signatoryId: selectedSignatoryId,
      signatoryName: signatory.name,
      signatoryTitle: signatory.title,
      signatureUrl: signatory.signatureUrl || undefined
    };

    if (editingReport) {
      reportData.id = editingReport.id;
    }

    await onSaveReport(reportData);
    setIsOpenFormModal(false);
  };

  // Filtered reports
  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      const matchesSearch = 
        r.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.reportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.subject.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = typeFilter === 'all' ? true : r.type === typeFilter;

      return matchesSearch && matchesType;
    });
  }, [reports, searchTerm, typeFilter]);

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="bg-teal-50 p-3 rounded-2xl">
            <FileText className="w-8 h-8 text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-900 tracking-tight">Technical & Service Reports</h1>
            <p className="text-slate-500 font-bold text-sm">Create and print landscape layout reports for customer care.</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={() => handleOpenNewReport('technical')}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-3.5 bg-teal-600 text-white rounded-2xl font-bold hover:bg-teal-700 transition-all active:scale-95 shadow-md shadow-teal-600/15"
          >
            <Plus className="w-4 h-4" />
            <span>Technical Report</span>
          </button>
          <button
            onClick={() => handleOpenNewReport('service')}
            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-3.5 bg-indigo-600 text-white rounded-2xl font-bold hover:bg-indigo-700 transition-all active:scale-95 shadow-md shadow-indigo-600/15"
          >
            <Plus className="w-4 h-4" />
            <span>Service Report</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm flex flex-wrap items-center gap-4">
        <div className="relative group flex-1 min-w-[280px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-teal-600 transition-colors" />
          <input 
            type="text"
            placeholder="Search reports by client, number or subject..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all placeholder:text-slate-400 text-sm font-medium"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-black uppercase tracking-widest text-slate-400 mr-2">Type:</span>
          <button
            onClick={() => setTypeFilter('all')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-bold transition-all",
              typeFilter === 'all' ? "bg-slate-950 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"
            )}
          >
            All Reports
          </button>
          <button
            onClick={() => setTypeFilter('technical')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-bold transition-all",
              typeFilter === 'technical' ? "bg-teal-600 text-white" : "bg-teal-50 text-teal-700 hover:bg-teal-100"
            )}
          >
            Technical Report
          </button>
          <button
            onClick={() => setTypeFilter('service')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-bold transition-all",
              typeFilter === 'service' ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            )}
          >
            Service Report
          </button>
        </div>
      </div>

      {/* Reports Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredReports.map((report) => (
          <div 
            key={report.id}
            className="bg-white p-6 rounded-[28px] border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group"
          >
            <div>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className={cn(
                  "p-3 rounded-2xl",
                  report.type === 'technical' ? "bg-teal-50 text-teal-600" : "bg-indigo-50 text-indigo-600"
                )}>
                  <FileText className="w-6 h-6" />
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {report.reportNumber}
                  </span>
                  <div className={cn(
                    "mt-1 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider inline-block",
                    report.type === 'technical' 
                      ? "bg-teal-50 text-teal-700 border border-teal-100" 
                      : "bg-indigo-50 text-indigo-700 border border-indigo-100"
                  )}>
                    {report.type === 'technical' ? 'Technical Report' : 'Report After Service'}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-bold text-slate-900 group-hover:text-teal-600 transition-colors line-clamp-1">
                  {report.clientName}
                </h3>
                <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  {format(new Date(report.date), 'MMMM dd, yyyy')}
                </p>
                <div className="text-xs font-bold text-slate-500 bg-slate-50/50 p-3 rounded-xl border border-slate-100/50 line-clamp-2">
                  <span className="text-[9px] font-black text-slate-400 block uppercase tracking-wider mb-0.5">Subject:</span>
                  {report.subject}
                </div>
              </div>

              {/* Items Summary */}
              <div className="mt-4 flex items-center justify-between border-t border-slate-50 pt-4">
                <span className="text-xs font-medium text-slate-400">
                  Equipment checked:
                </span>
                <span className="text-xs font-black text-slate-700 bg-slate-100 px-2.5 py-1 rounded-lg">
                  {report.items.length} units
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 border-t border-slate-100 grid grid-cols-2 gap-2">
              <button
                onClick={() => setViewingReport(report)}
                className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl text-xs font-bold transition-all active:scale-95"
              >
                <Eye className="w-4 h-4 text-slate-500" />
                <span>Preview</span>
              </button>
              <button
                onClick={() => onExportPDF(report, false)}
                className="flex items-center justify-center gap-1.5 py-2.5 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-xl text-xs font-bold transition-all active:scale-95"
              >
                <Download className="w-4 h-4 text-teal-600" />
                <span>PDF</span>
              </button>
              <button
                onClick={() => onExportPDF(report, true)}
                className="flex items-center justify-center gap-1.5 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition-all active:scale-95"
              >
                <Printer className="w-4 h-4 text-indigo-600" />
                <span>Print</span>
              </button>
              
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => handleOpenEditReport(report)}
                  className="flex items-center justify-center py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold transition-all active:scale-95"
                  title="Edit Report"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onDeleteReport(report.id)}
                  className="flex items-center justify-center py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-bold transition-all active:scale-95"
                  title="Delete Report"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {filteredReports.length === 0 && (
          <div className="bg-white p-12 rounded-[40px] text-center border-2 border-dashed border-slate-100 col-span-full">
            <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-slate-300" />
            </div>
            <p className="text-slate-400 font-bold">No reports found matching your search.</p>
          </div>
        )}
      </div>

      {/* Form Modal (Add / Edit) */}
      <AnimatePresence>
        {isOpenFormModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpenFormModal(false)}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-[40px] shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[90vh]">
                <div className={cn(
                  "p-8 border-b border-slate-100 flex items-center justify-between text-white",
                  formType === 'technical' ? "bg-teal-600" : "bg-indigo-600"
                )}>
                  <div>
                    <h2 className="text-2xl font-black tracking-tight">
                      {editingReport ? 'Edit ' : 'Generate New '}
                      {formType === 'technical' ? 'Technical Report' : 'Report After Service'}
                    </h2>
                    <p className="text-white/80 font-medium text-sm mt-1">
                      Fill out the details to produce a landscape-oriented document.
                    </p>
                  </div>
                  <button 
                    type="button" 
                    onClick={() => setIsOpenFormModal(false)}
                    className="p-2 hover:bg-white/10 rounded-2xl transition-all"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-8 overflow-y-auto space-y-6 flex-1">
                  
                  {/* Row 1: Proforma & Client */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {formType === 'technical' && (
                      <div className="space-y-2">
                        <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                          Select Proforma Invoice (Optional)
                        </label>
                        <select
                          value={selectedProformaId}
                          onChange={(e) => handleProformaChange(e.target.value)}
                          className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all"
                        >
                          <option value="">-- Generate without Proforma Invoice --</option>
                          {proformas.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.invoiceNumber} - {p.clientName}
                            </option>
                          ))}
                        </select>
                        <p className="text-[10px] text-slate-400 font-medium ml-1">
                          Selecting a proforma automatically populates client name and date.
                        </p>
                      </div>
                    )}

                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                        Client Name
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={selectedClientId}
                          onChange={(e) => {
                            setSelectedClientId(e.target.value);
                            if (e.target.value !== 'custom') {
                              setCustomClientName('');
                            }
                          }}
                          className="flex-1 px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all"
                        >
                          <option value="">-- Select Client --</option>
                          {clients.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                          <option value="custom">Input manually...</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Manual Client Name Field (only if custom is selected) */}
                  {selectedClientId === 'custom' && (
                    <div className="space-y-2 animate-in fade-in duration-300">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                        Enter Custom Client Name
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Tanzania Ports Authority"
                        value={customClientName}
                        onChange={(e) => setCustomClientName(e.target.value)}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all"
                      />
                    </div>
                  )}

                  {/* Row 2: Date & Subject */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                        Date of Draft
                      </label>
                      <input
                        type="date"
                        required
                        value={reportDate}
                        onChange={(e) => setReportDate(e.target.value)}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                        Subject Line
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. TECHNICAL REPORT FOR AIR CONDITIONERS SERVICE"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all"
                      />
                    </div>
                  </div>

                  {/* Signatory selection */}
                  <div className="space-y-2">
                    <label className="text-xs font-black uppercase tracking-widest text-slate-400 ml-1">
                      Selected Signatory
                    </label>
                    <select
                      value={selectedSignatoryId}
                      required
                      onChange={(e) => setSelectedSignatoryId(e.target.value)}
                      className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600/10 transition-all"
                    >
                      <option value="">-- Choose Signature --</option>
                      {signatories.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name} - {s.title}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-400 font-medium ml-1">
                      Auto-fills the signatory name, title, and inserts their uploaded signature.
                    </p>
                  </div>

                  {/* Items List Dynamic Table */}
                  <div className="space-y-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building className="w-5 h-5 text-slate-400" />
                        <h3 className="text-sm font-black uppercase tracking-wider text-slate-700">
                          Air Conditioner Units Details Table
                        </h3>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddItemRow}
                        className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all"
                      >
                        <PlusCircle className="w-4 h-4" />
                        <span>Add AC Unit Row</span>
                      </button>
                    </div>

                    <div className="space-y-4">
                      {items.map((item, index) => (
                        <div 
                          key={index} 
                          className="bg-slate-50 p-5 rounded-3xl border border-slate-100 space-y-4 relative animate-in fade-in slide-in-from-top-2 duration-200"
                        >
                          <div className="flex items-center justify-between pb-3 border-b border-slate-200/50">
                            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                              Row #{index + 1}
                            </span>
                            {items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveItemRow(index)}
                                className="p-1.5 hover:bg-red-50 text-slate-400 hover:text-red-500 rounded-lg transition-all"
                                title="Remove row"
                              >
                                <MinusCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                Type of AC
                              </label>
                              <input
                                type="text"
                                required
                                placeholder="e.g. Split Unit 12000BTU"
                                value={item.typeOfAC}
                                onChange={(e) => handleItemFieldChange(index, 'typeOfAC', e.target.value)}
                                className="w-full px-4 py-2.5 bg-white border border-slate-100 rounded-xl font-bold text-slate-900 focus:outline-none text-xs"
                              />
                            </div>

                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                Quantity
                              </label>
                              <input
                                type="number"
                                required
                                min="1"
                                value={item.qty}
                                onChange={(e) => handleItemFieldChange(index, 'qty', e.target.value)}
                                className="w-full px-4 py-2.5 bg-white border border-slate-100 rounded-xl font-bold text-slate-900 focus:outline-none text-xs"
                              />
                            </div>

                            <div className="space-y-1 md:col-span-2">
                              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                User / Department / Room
                              </label>
                              <input
                                type="text"
                                required
                                placeholder="e.g. Accounts Office"
                                value={item.userDept}
                                onChange={(e) => handleItemFieldChange(index, 'userDept', e.target.value)}
                                className="w-full px-4 py-2.5 bg-white border border-slate-100 rounded-xl font-bold text-slate-900 focus:outline-none text-xs"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {formType === 'technical' ? (
                              <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                  Checking Result
                                </label>
                                <textarea
                                  required
                                  rows={2}
                                  placeholder="e.g. Compressor faulty, capacitor blown."
                                  value={item.checkingResult}
                                  onChange={(e) => handleItemFieldChange(index, 'checkingResult', e.target.value)}
                                  className="w-full px-4 py-2.5 bg-white border border-slate-100 rounded-xl font-medium text-slate-900 focus:outline-none text-xs"
                                />
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                  Performance Status
                                </label>
                                <textarea
                                  required
                                  rows={2}
                                  placeholder="e.g. Working in good cooling condition."
                                  value={item.performanceStatus}
                                  onChange={(e) => handleItemFieldChange(index, 'performanceStatus', e.target.value)}
                                  className="w-full px-4 py-2.5 bg-white border border-slate-100 rounded-xl font-medium text-slate-900 focus:outline-none text-xs"
                                />
                              </div>
                            )}

                            <div className="space-y-1">
                              <label className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                                Remedial Recommendations
                              </label>
                              <textarea
                                required
                                rows={2}
                                placeholder="e.g. Replace compressor and capacitor."
                                value={item.remedialRecommendations}
                                onChange={(e) => handleItemFieldChange(index, 'remedialRecommendations', e.target.value)}
                                className="w-full px-4 py-2.5 bg-white border border-slate-100 rounded-xl font-medium text-slate-900 focus:outline-none text-xs"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>

                <div className="p-8 border-t border-slate-100 bg-slate-50 flex gap-4">
                  <button 
                    type="button" 
                    onClick={() => setIsOpenFormModal(false)}
                    className="flex-1 py-4 bg-white border border-slate-200 text-slate-600 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-slate-50 transition-all shadow-sm active:scale-95"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className={cn(
                      "flex-2 py-4 text-white rounded-2xl font-black uppercase tracking-widest text-xs transition-all shadow-lg active:scale-95 flex items-center justify-center gap-2",
                      formType === 'technical' ? "bg-teal-600 hover:bg-teal-700 shadow-teal-600/20" : "bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20"
                    )}
                  >
                    <FileCheck className="w-5 h-5" />
                    <span>Save & Generate Report</span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {viewingReport && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewingReport(null)}
              className="absolute inset-0 bg-blue-950/50 backdrop-blur-md"
            />

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-5xl bg-white rounded-[40px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            >
              {/* Header */}
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-950 text-white">
                <div>
                  <h3 className="font-black text-lg">Document Print Preview</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Landscape orientation formatted layout.</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onExportPDF(viewingReport, false); }}
                    className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-xl text-xs font-bold hover:bg-teal-700 transition-all active:scale-95 shadow-md shadow-teal-600/10"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                  <button
                    onClick={() => { onExportPDF(viewingReport, true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all active:scale-95 shadow-md shadow-indigo-600/10"
                  >
                    <Printer className="w-4 h-4" />
                    <span>Print</span>
                  </button>
                  <button 
                    onClick={() => setViewingReport(null)}
                    className="p-2 hover:bg-white/10 rounded-xl transition-all"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Landscape Document Canvas Sheet */}
              <div className="p-10 overflow-y-auto bg-slate-100 flex-1 flex justify-center">
                <div className="w-full max-w-[1000px] bg-white p-12 rounded-[24px] shadow-lg border border-slate-200/50 font-sans text-slate-800 text-xs min-h-[600px] flex flex-col justify-between">
                  <div>
                    {/* Fallback Letterhead or settings image */}
                    {settings?.letterheadUrl ? (
                      <div className="w-full mb-8">
                        <img 
                          src={settings.letterheadUrl} 
                          alt="Company Letterhead" 
                          referrerPolicy="no-referrer"
                          className="w-full max-h-[100px] object-contain" 
                        />
                      </div>
                    ) : (
                      <div className="border-b-4 border-blue-900 pb-4 mb-8 text-center space-y-1">
                        <h2 className="text-base font-black text-blue-900">BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED</h2>
                        <p className="text-[10px] text-slate-500 font-bold tracking-wider">HVAC & ELECTRICAL SYSTEMS ENGINEERING SERVICE REPORT</p>
                      </div>
                    )}

                    {/* Metadata */}
                    <div className="flex justify-between items-start font-bold uppercase text-[10px] text-slate-700 mb-6 border-b border-slate-100 pb-4">
                      <div>
                        <span className="text-slate-400 block font-black mb-0.5">CLIENT:</span>
                        <span className="text-sm text-slate-900 font-black">{viewingReport.clientName}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-slate-400 block font-black mb-0.5">DATE:</span>
                        <span className="text-sm text-slate-900 font-black">
                          {format(new Date(viewingReport.date), 'MMMM dd, yyyy')}
                        </span>
                      </div>
                    </div>

                    {/* Subject line */}
                    <div className="mb-6">
                      <span className="text-[9px] font-black text-slate-400 block uppercase mb-1">Subject:</span>
                      <h4 className="text-sm font-black text-slate-900 uppercase border-b-2 border-slate-950 pb-2">
                        {viewingReport.subject}
                      </h4>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto mb-8 border border-slate-200 rounded-xl overflow-hidden">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-950 text-white font-black uppercase text-[9px] tracking-wider">
                            <th className="py-3 px-4 border border-slate-800 text-center w-[60px]">S/No</th>
                            <th className="py-3 px-4 border border-slate-800">Type of Air-Conditioner</th>
                            <th className="py-3 px-4 border border-slate-800 text-center w-[60px]">Qty</th>
                            <th className="py-3 px-4 border border-slate-800">User / Dept</th>
                            {viewingReport.type === 'technical' ? (
                              <th className="py-3 px-4 border border-slate-800">Checking Result</th>
                            ) : (
                              <th className="py-3 px-4 border border-slate-800">Performance Status</th>
                            )}
                            <th className="py-3 px-4 border border-slate-800">Remedial Recommendations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewingReport.items.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50 font-medium text-slate-700">
                              <td className="py-3 px-4 border border-slate-100 text-center font-bold">{item.sn}</td>
                              <td className="py-3 px-4 border border-slate-100 font-bold text-slate-900">{item.typeOfAC}</td>
                              <td className="py-3 px-4 border border-slate-100 text-center font-bold">{item.qty}</td>
                              <td className="py-3 px-4 border border-slate-100">{item.userDept}</td>
                              <td className="py-3 px-4 border border-slate-100 italic bg-slate-50/30">
                                {viewingReport.type === 'technical' ? item.checkingResult : item.performanceStatus}
                              </td>
                              <td className="py-3 px-4 border border-slate-100 text-teal-800 font-medium">{item.remedialRecommendations}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Text block */}
                    <div className="space-y-4 text-slate-600 font-medium text-[11px] leading-relaxed max-w-[800px]">
                      {viewingReport.type === 'technical' ? (
                        <p>
                          We have attached our Proforma Invoice No. <strong className="text-slate-900 font-black">{viewingReport.proformaNumber || 'N/A'}</strong> for the cost of repairing your split unit AC in the mentioned room for your perusal and approval.
                        </p>
                      ) : null}
                      <p>
                        Thanking you in advance for the opportunity of serving you, while anticipating the receipt of your L.P.O for the above works.
                      </p>
                    </div>
                  </div>

                  {/* Signatures */}
                  <div className="mt-12 pt-8 border-t border-slate-100 flex justify-between items-end">
                    <div>
                      <p className="font-bold text-slate-800 text-[10px] mb-2 uppercase">Yours faithfully,</p>
                      <p className="font-black text-slate-900 uppercase text-[10px] mb-4">
                        BRIGHT TECHNICAL SYSTEMS AND GENERAL SUPPLIES LIMITED
                      </p>
                      
                      {viewingReport.signatureUrl && (
                        <div className="mb-2 h-12 flex items-center">
                          <img 
                            src={viewingReport.signatureUrl} 
                            alt="Signature" 
                            referrerPolicy="no-referrer"
                            className="max-h-full object-contain" 
                          />
                        </div>
                      )}

                      <p className="font-black text-slate-900 uppercase text-[11px]">{viewingReport.signatoryName}</p>
                      <p className="font-bold text-slate-500 text-[10px]">{viewingReport.signatoryTitle}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
